import styled, { keyframes } from "styled-components";

// Animations

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

// Styled Components
export const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  overflow-x: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 20% 30%,
        rgba(124, 14, 51, 0.1) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 70%,
        rgba(239, 219, 203, 0.1) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 40% 80%,
        rgba(124, 14, 51, 0.08) 0%,
        transparent 50%
      );

    z-index: -1;
  }

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 10px;
  }
`;

export const AuthHeader = styled.div`
  text-align: center;
  z-index: 10;
  animation: ${slideIn} 0.8s ease-out;

  @media (max-width: 768px) {
    position: static;
    transform: none;
    margin-bottom: 30px;
  }
`;

export const LogoImage = styled.img`
  max-height: 80px;
  width: auto;
  position: absolute;
  bottom: 40px;
  left: 40px;
  filter: drop-shadow(0 4px 12px rgba(124, 14, 51, 0.2));

  @media (max-width: 768px) {
    display: none;
  }
`;

export const Logo = styled.a`
  font-family: "Source Code Pro", monospace;
  font-size: 32px;
  font-weight: 700;
  color: var(--accent);
  text-decoration: none;
  letter-spacing: 2px;
  text-transform: uppercase;
  display: block;
  margin-bottom: 10px;
  animation: ${float} 3s ease-in-out infinite;

  &::before {
    content: "♡ ";
    color: var(--accent-dark);
    font-size: 28px;
  }

  &::after {
    content: " ♡";
    color: var(--accent-dark);
    font-size: 28px;
  }

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

export const AuthSubtitle = styled.p`
  color: var(--text-muted);
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

export const CardContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 450px;
  height: ${(props) => (!props.$isFlipped ? "650px" : "560px")};
  perspective: 1000px;
  animation: ${slideIn} 1s ease-out 0.3s both;

  @media (max-width: 768px) {
    max-width: 100%;
    height: ${(props) => (!props.$isFlipped ? "580px" : "510px")};
  }
`;

export const Card = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  transform: ${(props) =>
    props.$isFlipped ? "rotateY(180deg)" : "rotateY(0deg)"};
`;

export const CardFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  background: var(--surface);
  border-radius: 20px;
  padding: 30px 40px;
  box-shadow: var(--shadow-strong);
  border: 2px solid var(--accent-light);
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;

  &:hover {
    /* border-color: var(--accent); */
  }

  @media (max-width: 768px) {
    padding: 25px 25px;
    border-radius: 15px;
  }

  @media (max-width: 480px) {
    padding: 20px 20px;
  }
`;

export const CardFront = styled(CardFace)``;

export const CardBack = styled(CardFace)`
  transform: rotateY(180deg);
`;

export const FormHeader = styled.div`
  text-align: center;
  margin-bottom: 25px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    margin-bottom: 18px;
  }
`;

export const FormTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

export const FormDescription = styled.p`
  color: var(--text-muted);
  font-size: 14px;
  line-height: 1.5;
`;

export const Form = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const FormGroup = styled.div`
  margin-bottom: 18px;
  position: relative;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    margin-bottom: 14px;
  }
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--text-secondary);
`;

export const Input = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid var(--accent-light);
  border-radius: 25px;
  font-family: inherit;
  font-size: 14px;
  background: var(--surface);
  color: var(--text-primary);
  transition: all 0.3s ease;
  outline: none;

  &:focus {
    border-color: var(--accent);
    background: var(--surface);
    box-shadow: 0 0 0 3px rgba(124, 14, 51, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  @media (max-width: 768px) {
    padding: 14px 18px;
    font-size: 16px;
    border-radius: 20px;
  }

  @media (max-width: 480px) {
    padding: 12px 16px;
  }
`;

export const PasswordGroup = styled.div`
  position: relative;
  width: 100%;
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 5px;
  transition: color 0.3s ease;

  &:hover {
    color: var(--accent);
  }
`;

export const SubmitButton = styled.button`
  width: 100%;
  background: var(--gradient-primary);
  color: var(--surface);
  border: none;
  padding: 14px;
  border-radius: 25px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;

  margin: 15px 0 10px 0;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  opacity: ${(props) => (props.$isLoading ? 0.8 : 1)};
  pointer-events: ${(props) => (props.$isLoading ? "none" : "auto")};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: var(--shadow-medium);
    background: var(--accent-dark);
    color: var(--surface);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 12px;
    font-size: 14px;
    border-radius: 20px;
    margin: 12px 0 8px 0;
  }

  @media (max-width: 480px) {
    padding: 11px;
  }
`;

export const Message = styled.div`
  padding: 12px 20px;
  border-radius: 20px;
  margin-bottom: 20px;
  font-size: 12px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
  animation: ${slideIn} 0.3s ease-out;
  background: ${(props) =>
    props.$type === "success" ? "var(--success)" : "var(--error)"};
  color: var(--surface);
`;

export const ForgotPassword = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 12px;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 0.3s ease;
  text-align: center;
  margin: 8px 0 8px 0;

  &:hover {
    color: var(--accent);
  }

  @media (max-width: 768px) {
    margin: 6px 0 6px 0;
  }
`;

export const SwitchForm = styled.div`
  text-align: center;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid var(--accent-light);

  @media (max-width: 768px) {
    padding-top: 10px;
  }

  @media (max-width: 480px) {
    padding-top: 8px;
  }
`;

export const SwitchText = styled.p`
  color: var(--text-muted);
  font-size: 14px;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 13px;
    margin-bottom: 8px;
  }
`;

export const SwitchButton = styled.button`
  background: transparent;
  border: 2px solid var(--accent-light);
  color: var(--accent);
  padding: 10px 25px;
  border-radius: 25px;
  font-family: inherit;
  font-size: 12px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 auto;

  &:hover {
    background: var(--accent);
    color: var(--surface);
    border-color: var(--accent);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 8px 20px;
    font-size: 11px;
    border-radius: 20px;
  }

  @media (max-width: 480px) {
    padding: 8px 18px;
  }
`;

export const PasswordRequirements = styled.ul`
  list-style: none;
  padding: 0;
  margin: 6px 0 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
`;

export const PasswordRequirement = styled.li`
  font-size: 11px;
  color: ${(props) => (props.$met ? "var(--success, #28a745)" : "var(--text-muted)")};
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.2s ease;

  svg {
    font-size: 10px;
  }
`;

export const BackButton = styled.button`
  position: absolute;
  top: 30px;
  left: 30px;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid var(--accent-light);
  color: var(--accent);
  padding: 10px 25px;
  border-radius: 25px;
  font-family: inherit;
  font-size: 12px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 auto;

  &:hover {
    background: var(--accent);
    color: var(--surface);
    border-color: var(--accent);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    top: 20px;
    left: 20px;
    padding: 10px 16px;
    font-size: 13px;
  }

  @media (max-width: 480px) {
    padding: 8px 14px;
    font-size: 12px;
  }
`;
